import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

interface OrgLinkChipProps {
  organizationName: string;
  positionTitle?: string;
  isActive?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onLinkClick?: (e: React.MouseEvent) => void;
  maxLength?: number; // Optional prop to limit characters
}

export const OrgLinkChip = ({
  organizationName,
  positionTitle,
  isActive = true,
  onClick,
  onLinkClick,
  maxLength = 40
}: OrgLinkChipProps) => {
  const isClickable = !!onClick;

  // Helper to truncate text
  const truncateText = (text: string, length: number) => {
    if (text.length <= length) return text;
    return text.slice(0, length) + '...';
  };

  const isPositionTruncated = positionTitle ? positionTitle.length > maxLength : false;
  const isOrgTruncated = organizationName.length > maxLength;

  return (
    <Box
      onClick={(e) => isClickable && onClick && onClick(e)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: positionTitle ? 32 : 24, 
        px: 1,
        borderRadius: 2, 
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08), 
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.1)',
        cursor: isClickable ? 'pointer' : 'default',
        opacity: isActive ? 1 : 0.5,
        transition: 'opacity 0.2s, filter 0.2s',
        userSelect: 'none',
        '&:hover': isClickable ? { filter: 'brightness(0.95)' } : {},
      }}
    >
      {/* Container for the Text Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        
        {/* Organization Name */}
        <Tooltip title={isOrgTruncated ? organizationName : ""} placement="top-start" disableHoverListener={!isOrgTruncated}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(0,0,0,0.67)',
              fontWeight: 500,
              lineHeight: positionTitle ? 1.2 : 1, 
              whiteSpace: 'nowrap'
            }}
          >
            {truncateText(organizationName, maxLength)}
          </Typography>
        </Tooltip>

        {/* Position Title under Organization Name */}
        {positionTitle && (
          <Tooltip title={isPositionTruncated ? positionTitle : ""} placement="bottom-start" disableHoverListener={!isPositionTruncated}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(0,0,0,0.67)',
                fontSize: '0.65rem', 
                lineHeight: 1,
                opacity: 0.85,
                textDecoration: isActive ? 'none' : 'line-through',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(positionTitle, maxLength)}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Optional Link Button at the tail */}
      {onLinkClick && (
        <Box
          component="span"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation(); 
            onLinkClick(e);
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            ml: 0.5,
            mr: -0.5,
            p: 0.25,
            borderRadius: '50%',
            cursor: 'pointer',
            color: 'primary.main', 
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.08)',
              color: 'primary.dark'
            }
          }}
        >
          <Edit sx={{ fontSize: 14 }} /> 
        </Box>
      )}
    </Box>
  );
};